import React, {useEffect, useState, useRef} from 'react';
import useCommon from '@utils/hooks/common';
import classNames from 'classnames';
import styles from './styles';

interface TournamentNotificationProps {
  targetRef: React.RefObject<HTMLElement>;
  message: string;
  isVisible: boolean;
  onClose: () => void;
}

const TournamentNotification: React.FC<TournamentNotificationProps> = ({
  targetRef,
  message,
  isVisible,
  onClose,
}) => {
  const {classes} = useCommon({
    withStyles: styles,
  });

  const notificationRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({top: 0, left: 0});

  // Update position when target element moves or resizes
  useEffect(() => {
    if (!isVisible || !targetRef.current) return;

    const updatePosition = () => {
      if (!targetRef.current || !notificationRef.current) return;

      const targetRect = targetRef.current.getBoundingClientRect();
      const notificationRect = notificationRef.current.getBoundingClientRect();

      // Calculate the horizontal position
      let left =
        targetRect.left + targetRect.width / 2 - notificationRect.width / 2 + window.scrollX;

      // Make sure the notification doesn't go off-screen on the left
      if (left < 10) left = 10;

      // Make sure the notification doesn't go off-screen on the right
      const rightEdge = left + notificationRect.width;
      if (rightEdge > window.innerWidth - 10) {
        left = window.innerWidth - notificationRect.width - 10;
      }

      // Position the notification below the target element
      setPosition({
        top: targetRect.bottom + window.scrollY + 10, // 10px gap
        left,
      });
    };

    // Initial position
    // Use a small timeout to ensure the notification has rendered fully
    setTimeout(updatePosition, 0);

    // Update position on resize and scroll
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    // Auto-hide after 10 seconds
    const timer = setTimeout(() => {
      onClose();
    }, 3000);

    // Cleanup function
    return function cleanup() {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
      clearTimeout(timer);
    };
  }, [isVisible, targetRef, onClose]);

  if (!isVisible) return null;

  return (
    <div
      ref={notificationRef}
      className={classes.tournamentNotification}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
      onClick={onClose}
    >
      {message}
    </div>
  );
};

export default TournamentNotification;
